<template>
    <div class="ImportValidation">
        <WCard>
            <div class="Header flex flex-wrap row-stretch-center">
                <div class="Notifications mb-1">
                    <FadeTransition mode="out-in">
                        <ListStatus
                            v-if="importData"
                            v-bind="importData"
                            class="flex row-left-center"
                        />
                    </FadeTransition>
                </div>
            </div>

            <div
                v-if="issues && issues.length"
                class="Notifications"
            >
                <template v-for="type in issuesTypes">
                    <FadeTransition
                        v-if="(issuesByType[type] || []).length"
                        :key="`issues-${type}`"
                        mode="out-in"
                    >
                        <ImportNotifications
                            :notifications="issuesByType[type]"
                            :type="type"
                            collapsable
                            class="mb-1"
                        />
                    </FadeTransition>
                </template>
            </div>

            <div class="Body">
                <DataGrid
                    v-if="schema"
                    v-bind="dataGridConfig"
                    :loading="isLoadingSchema"
                    :columns="schema.columns"
                    :rows="schema.rows"
                    class="DataGrid"
                />
            </div>
        </WCard>
    </div>
</template>

<script>
import { Api, apiRequest, notifyError } from '@/api'
import { entityMixin } from '@/mixins'
import { DataGrid, ListStatus } from '@/components'
import { STATUS } from '@/entities/imports'
import { ImportNotifications } from '../components'
import Grid from './grid'

const DATAGRID_CONFIG = {
    hasInputFilter: false,
    filterable: false,
    sideBar: false,
    cleanFiltersButton: false,
    editable: false,
}

const ISSUES_TYPES = {
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info',
    SUCCESS: 'success',
}

export default {
    name: 'ImportPreview',
    components: {
        ListStatus,
        ImportNotifications,
        DataGrid,
    },
    mixins: [entityMixin],
    metaInfo () {
        return {
            title: this.$t('admin.navigation.imports.create'),
        }
    },
    entity () {
        return {
            breadcrumbs: [
                {
                    key: 'AdminImportsList',
                    label: this.$t('imports.create.title'),
                    to: { name: 'AdminImportsList' },
                },
                {
                    key: 'AdminImportsPreview',
                    label: this.$t('imports.steps.preview'),
                },
            ],
        }
    },
    data () {
        return {
            importData: undefined,
            isLoading: false,

            schema: undefined,
            isLoadingSchema: false,

            issues: [],
            issuesTypes: Object.values(ISSUES_TYPES),
        }
    },
    computed: {
        isCompleted () {
            const { status } = this.importData || {}
            return [STATUS.completed].includes(status)
        },
        issuesByType () {
            const KEY_TYPE = 'level'
            return (this.issues || []).reduce((acc, item) => ({
                ...acc,
                [item[KEY_TYPE]]: [...(acc[item[KEY_TYPE]] || []), item],
            }), {})
        },
    },
    mounted () {
        const { id } = this.$route.params || {}
        if ([undefined, null].includes(id)) this.$router.back()
        else this.getImport(id)

        this.id = id
        this.dataGridConfig = DATAGRID_CONFIG
    },
    methods: {
        handleConfirm () {
            this.$router.push({ name: 'AdminImportsList' })
        },
        handleError () {
            notifyError(this.$t('imports.create.modals.NOT_FOUND'))()
            this.$router.push({ name: 'AdminImportsList' })
        },
        fetchData ({ data }) {
            const {
                preview, import: importData, globalIssues, rowIssues, cellIssues,
            } = data
            const issues = Grid.normalizeIssues({ globalIssues, rowIssues, cellIssues })

            if (![STATUS.cancelled, STATUS.failed].includes(importData.status)) {
                if (preview) this.schema = preview
                this.importData = importData

                // Siempre mostrar todos los issues (global, row y cell)
                const allIssues = [
                    ...(issues.global || []),
                    ...(issues.row || []),
                    ...(issues.cell || []),
                ]

                if ([STATUS.completed].includes(importData.status)) {
                    // Si está completado, agregar mensaje de éxito
                    this.issues = [
                        {
                            type: 'success',
                            level: 'success',
                            scope: 'global',
                            message: this.$t('imports.create.notifications.successText'),
                        },
                        ...allIssues,
                    ]
                } else {
                    this.issues = allIssues
                }
            } else {
                this.handleError()
            }
        },
        async getImport (id) {
            this.isLoading = true
            apiRequest(Api().imports.getSource(id))
                .then(this.fetchData)
                .catch(this.handleError)
                .finally(() => (this.isLoading = false))
        },
    },
}
</script>
<style lang="scss" scoped>
.ImportValidation {
    .WCard {
        @include admin-screen-height(54px);
    }
}

.ListStatus {
    &::v-deep .Group {
        display: flex;
    }
    &::v-deep .Group + .Group, &::v-deep .Status + .Status {
        margin-left: 20px;
    }
}

.ImportNotifications {
    &::v-deep .Issue  {
        margin-bottom: 15px;
    }
}

.DataGrid {
	width: 100%;
	@include admin-screen-height(192px);
}
</style>
